import type {
  AccountCreateApiData,
  AccountCreateApiResponses,
  AccountListApiData,
  AccountListApiResponses,
  DeliveryNotificationRequestCreateApiData,
  DeliveryNotificationRequestCreateApiResponses,
  MfaVerifyApiData,
  MfaVerifyApiResponses,
  OrganizationSignupApiData,
  OrganizationSignupApiResponses,
  PasskeyAuthenticationChallengeApiData,
  PasskeyAuthenticationChallengeApiResponses,
  PasskeyAuthenticationVerifyApiData,
  PasskeyAuthenticationVerifyApiResponses,
  PasskeyDeleteApiData,
  PasskeyDeleteApiResponses,
  PasskeyListApiData,
  PasskeyListApiResponses,
  PasskeyRegistrationChallengeApiData,
  PasskeyRegistrationChallengeApiResponses,
  PasskeyRegistrationVerifyApiData,
  PasskeyRegistrationVerifyApiResponses,
  RecoveryCodeVerifyApiData,
  RecoveryCodeVerifyApiResponses,
  RecoveryCodesDisableApiData,
  RecoveryCodesDisableApiResponses,
  RecoveryCodesGenerateApiData,
  RecoveryCodesGenerateApiResponses,
  RecoveryCodesStatusApiData,
  RecoveryCodesStatusApiResponses,
  TotpConfirmApiData,
  TotpConfirmApiResponses,
  TotpDisableApiData,
  TotpDisableApiResponses,
  TotpEnrollStartApiData,
  TotpEnrollStartApiResponses,
  TotpStatusApiData,
  TotpStatusApiResponses,
  UserConfirmPersonalInfoApiData,
  UserConfirmPersonalInfoApiResponses,
  UserGetApiData,
  UserGetApiResponses,
  UserImpersonateApiData,
  UserImpersonateApiResponses,
  UserLoginByEmailApiData,
  UserLoginByEmailApiResponses,
  UserLoginByPhoneNumberVerificationApiData,
  UserLoginByPhoneNumberVerificationApiResponses,
  UserLoginByTokenApiData,
  UserLoginByTokenApiResponses,
  UserPasswordChangeApiData,
  UserPasswordChangeApiResponses,
  UserPasswordResetConfirmApiData,
  UserPasswordResetConfirmApiResponses,
  UserPasswordResetConfirmOtpApiData,
  UserPasswordResetConfirmOtpApiResponses,
  UserPasswordResetRequestApiData,
  UserPasswordResetRequestApiResponses,
  UserPersonalInfoStatusApiData,
  UserPersonalInfoStatusApiResponses,
  UserSelectAccountApiData,
  UserSelectAccountApiResponses,
  UserSendEmailVerificationApiData,
  UserSendEmailVerificationApiResponses,
  UserSendPhoneNumberVerificationCodeGetApiData,
  UserSendPhoneNumberVerificationCodeGetApiResponses,
  UserSendPhoneNumberVerificationCodePostApiData,
  UserSendPhoneNumberVerificationCodePostApiResponses,
  UserSignupReconciliationApiData,
  UserSignupReconciliationApiResponses,
  UserSignupReconciliationEmailLoginApiData,
  UserSignupReconciliationEmailLoginApiResponses,
  UserSignupReconciliationPreviewApiData,
  UserSignupReconciliationPreviewApiResponses,
  UserSnoozePersonalInfoPromptApiData,
  UserSnoozePersonalInfoPromptApiResponses,
  UserUpdateApiData,
  UserUpdateApiResponses,
  UserUpdateContactApiData,
  UserUpdateContactApiResponses
} from '@navio/nsdk';

import type { ApiInput } from '../../utils';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import { accountMeUrls } from './accountMeUrls';

// Registered by `impersonationBypass`, not the domain map, so call sites use `...IgnoringImpersonation`.
export const getPaginatedAccountsEndpoint = dataEndpoint<
  AccountListApiData,
  AccountListApiResponses,
  ApiInput<AccountListApiData> & {
    /** Users who belong to a group that is a member of these programs. */
    membershipProgramIds?: number[];
    /** Users with an unused coupon in these referral coupon groups. */
    couponGroupIds?: number[];
    /** Min currently owned cars across the user's groups. */
    numCarsGte?: number;
    /** Max currently owned cars across the user's groups. */
    numCarsLte?: number;
    /** Initial brand attribution on any of the user's groups. */
    brandRelationshipIds?: number[];
    /** Release channel on any of the user's groups. */
    releaseChannels?: string[];
    /** User group type (personal, organization, group, ...). */
    userGroupTypes?: string[];
    /** personal_info_confirmed_at on a related user group, from date. */
    personalInfoConfirmedAtGte?: string;
    /** personal_info_confirmed_at on a related user group, to date. */
    personalInfoConfirmedAtLte?: string;
  }
>({
  getUrl: (queryParams) => apiUrl('accounts/', queryParams)
});

export const postImpersonateUserEndpoint = responseEndpoint<UserImpersonateApiData, UserImpersonateApiResponses>({
  getUrl: () => apiUrl('users/impersonate/'),
  getBody: (data) => data
});

export const getAccountUrls = {
  postSignupReconciliationPreview: responseEndpoint<
    UserSignupReconciliationPreviewApiData,
    UserSignupReconciliationPreviewApiResponses
  >({
    getUrl: () => apiUrl('users/signup-reconciliation/preview/'),
    getBody: (data) => data
  }),
  postSignupReconciliation: responseEndpoint<UserSignupReconciliationApiData, UserSignupReconciliationApiResponses>({
    getUrl: () => apiUrl('users/signup-reconciliation/'),
    getBody: (data) => data
  }),
  postSignupReconciliationEmailLogin: responseEndpoint<
    UserSignupReconciliationEmailLoginApiData,
    UserSignupReconciliationEmailLoginApiResponses
  >({
    getUrl: () => apiUrl('users/signup-reconciliation/email-login/'),
    getBody: (data) => data
  }),
  postAccount: responseEndpoint<AccountCreateApiData, AccountCreateApiResponses>({
    getUrl: () => apiUrl('accounts/'),
    getBody: (args) => args
  }),
  patchAccount: responseEndpoint<UserUpdateApiData, UserUpdateApiResponses>({
    getUrl: ({ userId }) => apiUrl(`users/${userId}/`),
    getBody: (args) => args
  }),
  postUpdateContact: responseEndpoint<UserUpdateContactApiData, UserUpdateContactApiResponses>({
    getUrl: () => apiUrl('users/me/update-contact/'),
    getBody: (data) => data
  }),
  ...accountMeUrls,
  postSendEmailVerification: responseEndpoint<UserSendEmailVerificationApiData, UserSendEmailVerificationApiResponses>({
    getUrl: () => apiUrl('users/send-email-verification/'),
    getBody: (data) => data
  }),
  postSendPhoneNumberVerification: responseEndpoint<
    UserSendPhoneNumberVerificationCodePostApiData,
    UserSendPhoneNumberVerificationCodePostApiResponses
  >({
    getUrl: () => apiUrl('users/send-phone-number-verification-v2/'),
    getBody: (data) => data
  }),
  getAccount: dataEndpoint<UserGetApiData, UserGetApiResponses>({
    getUrl: ({ userId }) => apiUrl(`users/${userId}/`)
  }),
  // The review is a per-brand opt-in, so the backend needs to know which brand is asking.
  getPersonalInfoStatus: dataEndpoint<UserPersonalInfoStatusApiData, UserPersonalInfoStatusApiResponses>({
    getUrl: ({ brandDomain }) => apiUrl('users/me/personal-info-status/', { brandDomain })
  }),
  postConfirmPersonalInfo: responseEndpoint<UserConfirmPersonalInfoApiData, UserConfirmPersonalInfoApiResponses>({
    getUrl: () => apiUrl('users/me/confirm-personal-info/'),
    getBody: (data) => data
  }),
  // Postpones the review prompt without stamping the confirmation — dismissing it is not the same
  // as having reviewed the details. Takes the brand because the backend caps the snooze at that
  // brand's review interval, so "remind me later" can never outlast the review cycle.
  postSnoozePersonalInfoPrompt: responseEndpoint<
    UserSnoozePersonalInfoPromptApiData,
    UserSnoozePersonalInfoPromptApiResponses
  >({
    getUrl: () => apiUrl('users/me/snooze-personal-info-prompt/'),
    getBody: (data) => data
  }),
  postEmailLogin: responseEndpoint<UserLoginByEmailApiData, UserLoginByEmailApiResponses>({
    getUrl: () => apiUrl('login/'),
    getBody: (data) => data
  }),
  postVerifyPhoneNumber: responseEndpoint<
    UserLoginByPhoneNumberVerificationApiData,
    UserLoginByPhoneNumberVerificationApiResponses
  >({
    getUrl: () => apiUrl('users/verify-phone-number/'),
    getBody: (data) => data
  }),
  postSelectAccount: responseEndpoint<UserSelectAccountApiData, UserSelectAccountApiResponses>({
    getUrl: () => apiUrl('users/select-account/'),
    getBody: (data) => data
  }),
  getPhoneNumberVerification: dataEndpoint<
    UserSendPhoneNumberVerificationCodeGetApiData,
    UserSendPhoneNumberVerificationCodeGetApiResponses
  >({
    getUrl: (queryParams) => apiUrl('users/send-phone-number-verification/', queryParams)
  }),
  postTokenLogin: responseEndpoint<
    UserLoginByTokenApiData,
    UserLoginByTokenApiResponses,
    ApiInput<UserLoginByTokenApiData> & { token: string }
  >({
    getUrl: () => apiUrl('login-token/'),
    getBody: (data) => data,
    getHeaders: ({ token }) => ({
      Authorization: `Token ${token}`
    })
  }),
  postUserWithOrganization: responseEndpoint<OrganizationSignupApiData, OrganizationSignupApiResponses>({
    getUrl: () => apiUrl('organizations/signup/'),
    getBody: (args) => args
  }),
  postDeliveryNotificationRequest: responseEndpoint<
    DeliveryNotificationRequestCreateApiData,
    DeliveryNotificationRequestCreateApiResponses
  >({
    getUrl: () => apiUrl('delivery-notification-requests/'),
    getBody: (args) => args
  }),
  postPasswordReset: responseEndpoint<UserPasswordResetRequestApiData, UserPasswordResetRequestApiResponses>({
    getUrl: () => apiUrl('users/password-reset/'),
    getBody: (data) => data
  }),
  postPasswordResetConfirm: responseEndpoint<UserPasswordResetConfirmApiData, UserPasswordResetConfirmApiResponses>({
    getUrl: () => apiUrl('users/password-reset/confirm/'),
    getBody: (data) => data
  }),
  postPasswordResetConfirmOtp: responseEndpoint<
    UserPasswordResetConfirmOtpApiData,
    UserPasswordResetConfirmOtpApiResponses
  >({
    getUrl: () => apiUrl('users/password-reset/confirm-otp/'),
    getBody: (data) => data
  }),
  postPasswordChange: responseEndpoint<UserPasswordChangeApiData, UserPasswordChangeApiResponses>({
    getUrl: () => apiUrl('users/password/change/'),
    getBody: (data) => data
  }),
  postPasskeyAuthenticationChallenge: responseEndpoint<
    PasskeyAuthenticationChallengeApiData,
    PasskeyAuthenticationChallengeApiResponses
  >({
    getUrl: () => apiUrl('users/passkeys/challenge/'),
    getBody: (data) => data
  }),
  postPasskeyRegistrationChallenge: responseEndpoint<
    PasskeyRegistrationChallengeApiData,
    PasskeyRegistrationChallengeApiResponses
  >({
    getUrl: () => apiUrl('users/passkeys/register/challenge/'),
    getBody: (data) => data
  }),
  postPasskeyRegistrationVerify: responseEndpoint<
    PasskeyRegistrationVerifyApiData,
    PasskeyRegistrationVerifyApiResponses
  >({
    getUrl: () => apiUrl('users/passkeys/register/verify/'),
    getBody: (data) => data
  }),
  postPasskeyAuthenticationVerify: responseEndpoint<
    PasskeyAuthenticationVerifyApiData,
    PasskeyAuthenticationVerifyApiResponses
  >({
    getUrl: () => apiUrl('users/passkeys/verify/'),
    getBody: (data) => data
  }),
  getPasskeys: dataEndpoint<PasskeyListApiData, PasskeyListApiResponses>({
    getUrl: (queryParams) => apiUrl('users/passkeys/', queryParams)
  }),
  deletePasskey: dataEndpoint<PasskeyDeleteApiData, PasskeyDeleteApiResponses>({
    getUrl: ({ factorSid }) => apiUrl(`users/passkeys/${factorSid}/`)
  }),
  getRecoveryCodesStatus: dataEndpoint<RecoveryCodesStatusApiData, RecoveryCodesStatusApiResponses>({
    getUrl: () => apiUrl('users/recovery-codes/')
  }),
  postGenerateRecoveryCodes: responseEndpoint<RecoveryCodesGenerateApiData, RecoveryCodesGenerateApiResponses>({
    getUrl: () => apiUrl('users/recovery-codes/'),
    getBody: (data) => data
  }),
  deleteRecoveryCodes: dataEndpoint<RecoveryCodesDisableApiData, RecoveryCodesDisableApiResponses>({
    getUrl: () => apiUrl('users/recovery-codes/')
  }),
  postRecoveryCodeVerify: responseEndpoint<RecoveryCodeVerifyApiData, RecoveryCodeVerifyApiResponses>({
    getUrl: () => apiUrl('users/recovery-codes/verify/'),
    getBody: (data) => data
  }),
  postLoginMfaVerify: responseEndpoint<MfaVerifyApiData, MfaVerifyApiResponses>({
    getUrl: () => apiUrl('login/mfa/verify/'),
    getBody: (data) => data
  }),
  postTotpEnroll: responseEndpoint<TotpEnrollStartApiData, TotpEnrollStartApiResponses>({
    getUrl: () => apiUrl('users/totp/enroll/'),
    getBody: (data) => data
  }),
  postTotpConfirm: responseEndpoint<TotpConfirmApiData, TotpConfirmApiResponses>({
    getUrl: () => apiUrl('users/totp/confirm/'),
    getBody: (data) => data
  }),
  getTotpStatus: dataEndpoint<TotpStatusApiData, TotpStatusApiResponses>({
    getUrl: () => apiUrl('users/totp/')
  }),
  postTotpDisable: responseEndpoint<TotpDisableApiData, TotpDisableApiResponses>({
    getUrl: () => apiUrl('users/totp/disable/'),
    getBody: (data) => data
  })
};
